import { c as _c } from "react/compiler-runtime";
import AddChatIcon from '@precis-digital/kurama/icons/AddChatIcon';
import Button from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import ReloadIcon from '@precis-digital/kurama/icons/ReloadIcon';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import ThemedImage from 'shared/components/Themed/Image';
import manWithPhone from 'public/assets/images/man_w_phone.png';
import { useCallback, type ReactElement } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { useTranslation } from 'shared/translations';
interface CopilotDrawerErrorStateProps {
  /** Clears the error boundary and re-renders the drawer content. */
  onRetry: () => void;
}

/**
 * Fallback shown inside the Copilot drawer when its content throws an
 * unexpected error. Keeps the drawer usable with retry and new chat actions
 * instead of leaving a blank body.
 */
const CopilotDrawerErrorState = t0 => {
  const $ = _c(34);
  const {
    onRetry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot.errorState"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const {
    createNewChat
  } = useCopilot();
  let t2;
  if ($[1] !== createNewChat || $[2] !== onRetry) {
    t2 = () => {
      createNewChat();
      onRetry();
    };
    $[1] = createNewChat;
    $[2] = onRetry;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleNewChat = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <StyledImageContainer><StyledErrorImage src={manWithPhone} alt="Man with phone" priority={true} width={204} height={183} /></StyledImageContainer>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("title");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <Typography variant="h4">{t4}</Typography>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("body");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <StyledBody variant="body3">{t6}</StyledBody>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <Grid display="flex" flexDirection="column" alignItems="center" gap="8px">{t5}{t7}</Grid>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AddChatIcon />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("newChat");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== handleNewChat || $[20] !== t10) {
    t11 = <Button variant="filled" leftIcon={t9} onClick={handleNewChat}>{t10}</Button>;
    $[19] = handleNewChat;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ReloadIcon />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t("retry");
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== onRetry || $[26] !== t13) {
    t14 = <Button variant="tonal" leftIcon={t12} onClick={onRetry}>{t13}</Button>;
    $[25] = onRetry;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <StyledButtonRow>{t11}{t14}</StyledButtonRow>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15 || $[32] !== t8) {
    t16 = <StyledContainer>{t3}{t8}{t15}</StyledContainer>;
    $[31] = t15;
    $[32] = t8;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
};
const StyledContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(4, 3),
  maxWidth: '380px',
  margin: '0 auto'
}));
const StyledImageContainer = styled(Grid)(() => ({
  width: '183px',
  height: '183px',
  position: 'relative',
  overflow: 'hidden'
}));
const StyledErrorImage = styled(ThemedImage)(() => ({
  position: 'absolute',
  left: '59px',
  top: '0px'
}));
const StyledButtonRow = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap'
}));
const StyledBody = styled(Typography)(({
  theme
}) => ({
  color: theme.tokens.foreground.main.secondary
}));
export default CopilotDrawerErrorState;